import { useNavigate, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import {
  useFinalizarValidacionMutation,
  useGetDocsListQuery,
} from "../../../services/docs";
import { ItemDocumentoValidar } from "../../../components/ItemDocumentoValidar";
import { DocumentacionAdicionalComponent } from "../../../components/DocumentacionAdicionalComponent";
import { Spinner } from "../../../components/Spinner";

export const PrevalidacionScreen = () => {
  const { siniestro, tramite_id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useGetDocsListQuery({
    siniestro,
    tramite: tramite_id,
  });

  const [finalizarValidacion, { isLoading: isLoadingCarga }] =
    useFinalizarValidacionMutation();

  const finalizarValidacionAction = async () => {
    try {
      const resp = await finalizarValidacion({
        siniestro,
        tramite_id: +(tramite_id || 0),
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Validación finalizada correctamente");
        navigate(`/view/siniestro/${siniestro}/${tramite_id}/proceso`);
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err?.data?.message || "Error al finalizar validación");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Corrección del arreglo "valor": Se pre-calculan los estados iniciales 
  // para evitar mutar el arreglo durante el renderizado (lo cual rompía React)
  const totalDocs = (data?.documentos?.length || 0) + (data?.adicionales?.length || 0);
  const valorState = Array.from({ length: totalDocs }, () => ({
    comentario: "",
    respuesta: 0,
    calificado: 0,
  }));

  return (
    <div className="space-y-6">

      {/* --- Barra de Acción Global y Leyenda --- */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col items-center justify-between gap-4 border-b border-slate-100 p-4 sm:flex-row">
          <div className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
            </div>
            Calificación de Expediente
          </div>

          {/* Botón de Finalizar (Solo se muestra si data.cop es true) */}
          {data?.cop && (
            <div className="flex w-full justify-end sm:w-auto">
              <button
                onClick={finalizarValidacionAction}
                disabled={isLoadingCarga}
                className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-brand-600/30 transition hover:bg-brand-700 disabled:opacity-50"
              >
                {isLoadingCarga ? <Spinner /> : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    Finalizar Validación
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Leyenda de Códigos de Estatus */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50/50 px-5 py-3 text-xs">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Código de Estatus:
          </span>
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-400 ring-2 ring-slate-100"></span>
              <span className="font-medium text-slate-600">No Cargado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100"></span>
              <span className="font-medium text-slate-700">Documento Cargado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-brand-500 ring-2 ring-brand-100"></span>
              <span className="font-medium text-slate-700">Digital Verificado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-amber-100"></span>
              <span className="font-medium text-slate-700">Digital Rechazado</span>
            </div>
          </div>
        </div>
      </section>

      {/* --- Grid: Documentos Titular --- */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-4 w-1.5 rounded bg-brand-600"></div>
            <h3 className="text-sm font-bold tracking-tight text-slate-800">
              Documentos del Titular
            </h3>
            <span className="text-xs font-normal text-slate-400">
              ({data?.documentos?.length || 0} documentos)
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {data?.documentos?.map((doc: any, i: number) => (
            <ItemDocumentoValidar
              key={doc.id}
              valor={valorState} // Se pasa el estado pre-calculado
              doc={doc}
              i={i}
              siniestro={siniestro}
              tramite_id={tramite_id}
            />
          ))}
        </div>
      </section>

      {/* --- Grid: Documentos Adicionales --- */}
      {data?.adicionales && data.adicionales.length > 0 && (
        <section className="space-y-4 pt-4">
          <div className="flex items-center justify-between border-t border-slate-200 pt-6">
            <div className="flex items-center gap-2">
              <div className="h-4 w-1.5 rounded bg-slate-400"></div>
              <h3 className="text-sm font-bold tracking-tight text-slate-800">
                Documentación Adicional
              </h3>
              <span className="text-xs font-normal text-slate-400">
                ({data.adicionales.length} documentos)
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {data.adicionales.map((doc: any, i: number) => {
              // Ajustamos el índice sumando los documentos titulares para mantener el flujo del arreglo "valor"
              const offsetIndex = i + (data?.documentos?.length || 0);
              return (
                <ItemDocumentoValidar
                  key={doc.id}
                  valor={valorState}
                  doc={doc}
                  i={offsetIndex}
                  siniestro={siniestro}
                  tramite_id={tramite_id}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* --- Sección de Solicitud de Documentos Extra --- */}
      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <DocumentacionAdicionalComponent />
      </section>

    </div>
  );
};
